// @ts-nocheck
import { useState } from "react";
import { useRoute, Link } from "wouter";
import { useGetCampaign, useListMessages, getGetCampaignQueryKey, getListMessagesQueryKey } from "../lib/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../components/layout/PageHeader";
import { StatCard } from "../components/StatCard";

const API = import.meta.env.VITE_API_URL ?? "";

const msgStatusStyle = (status: string) => {
  const map: Record<string, string> = {
    delivered: "bg-[#d4edda] text-[#155724]",
    sent: "bg-[#cce5ff] text-[#004085]",
    pending: "bg-[#fff3cd] text-[#856404]",
    failed: "bg-[#f8d7da] text-[#721c24]",
    read: "bg-[#e2d9f3] text-[#6f42c1]",
  };
  return map[status] ?? "bg-[#e9ecef] text-[#525f7f]";
};

const campaignStatusStyle = (status: string) => {
  const map: Record<string, string> = {
    completed: "bg-[#d4edda] text-[#155724]",
    running: "bg-[#cce5ff] text-[#004085]",
    paused: "bg-[#fff3cd] text-[#856404]",
    scheduled: "bg-[#e2d9f3] text-[#6f42c1]",
    failed: "bg-[#f8d7da] text-[#721c24]",
    draft: "bg-[#e9ecef] text-[#525f7f]",
  };
  return map[status] ?? "bg-[#e9ecef] text-[#525f7f]";
};

export function CampaignDetail() {
  const [, params] = useRoute("/campaigns/:id");
  const id = Number(params?.id);
  const qc = useQueryClient();
  const [actionLoading, setActionLoading] = useState(false);

  const isLive = (status?: string) => status === "running" || status === "paused";

  const { data: campaign, isLoading } = useGetCampaign(id, {
    query: {
      enabled: !!id,
      refetchInterval: (query) => {
        const status = (query.state.data as any)?.status;
        return isLive(status) ? 2000 : false;
      },
    },
  });

  const { data: messages } = useListMessages(
    { campaignId: id },
    {
      query: {
        enabled: !!id,
        refetchInterval: isLive(campaign?.status) ? 3000 : false,
      },
    }
  );

  const handleResend = async () => {
    if (!confirm("Resend this campaign to all contacts? Previous message history will be cleared.")) return;
    setActionLoading(true);
    try {
      await fetch(`${API}/api/campaigns/${id}/send`, { method: "POST" });
      qc.invalidateQueries({ queryKey: getGetCampaignQueryKey(id) });
      qc.invalidateQueries({ queryKey: getListMessagesQueryKey({ campaignId: id }) });
    } catch {
      alert("Failed to resend. Make sure your device is connected.");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePause = async () => {
    setActionLoading(true);
    try {
      await fetch(`${API}/api/campaigns/${id}/pause`, { method: "POST" });
      qc.invalidateQueries({ queryKey: getGetCampaignQueryKey(id) });
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    setActionLoading(true);
    try {
      await fetch(`${API}/api/campaigns/${id}/resume`, { method: "POST" });
      qc.invalidateQueries({ queryKey: getGetCampaignQueryKey(id) });
    } finally {
      setActionLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="grid grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-white rounded-xl border border-[#e9ecef]" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="p-6 text-center py-20">
        <i className="fas fa-exclamation-circle text-4xl text-[#cad1d7] mb-3 block" />
        <p className="text-[#8898aa]">Campaign not found</p>
        <Link href="/campaigns" className="text-[#1171ef] text-sm mt-2 inline-block hover:underline">
          ← Back to Bulk Messages
        </Link>
      </div>
    );
  }

  const total = (campaign as any).totalRecipients ?? 0;
  const sent = (campaign as any).sentCount ?? 0;
  const delivered = (campaign as any).deliveredCount ?? 0;
  const failed = (campaign as any).failedCount ?? 0;
  const pending = Math.max(0, total - sent - failed);
  const pct = total > 0 ? Math.round((sent / total) * 100) : 0;
  const status = campaign.status;

  const actions = [];
  if (status === "running") {
    actions.push({
      label: actionLoading ? "Pausing..." : "Pause",
      icon: "fas fa-pause",
      onClick: handlePause,
    });
  }
  if (status === "paused") {
    actions.push({
      label: actionLoading ? "Resuming..." : "Resume",
      icon: "fas fa-play",
      onClick: handleResume,
    });
  }
  if (status === "completed" || status === "failed") {
    actions.push({
      label: actionLoading ? "Resending..." : "Resend Campaign",
      icon: "fas fa-redo",
      onClick: handleResend,
    });
  }
  actions.push({ label: "Back", icon: "fas fa-arrow-left", onClick: () => window.history.back() });

  return (
    <div>
      <PageHeader title={campaign.name} breadcrumbs={["Bulk Messages"]} actions={actions} />

      <div className="px-6 pb-6 space-y-6">

        {/* Live status banner */}
        {status === "running" && (
          <div className="bg-[#cce5ff] border border-[#b8daff] rounded-xl px-5 py-3 flex items-center gap-3">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#004085] opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-[#004085]" />
            </span>
            <span className="text-sm font-medium text-[#004085]">
              Sending in progress — {sent} sent, {pending} remaining, {failed} failed
            </span>
          </div>
        )}
        {status === "paused" && (
          <div className="bg-[#fff3cd] border border-[#ffc107] rounded-xl px-5 py-3 flex items-center gap-3">
            <i className="fas fa-pause-circle text-[#856404]" />
            <span className="text-sm font-medium text-[#856404]">
              Campaign paused — {sent} sent, {pending} remaining. Click Resume to continue.
            </span>
          </div>
        )}

        {/* Stat cards */}
        <div className="grid grid-cols-4 gap-4">
          <StatCard label="Total Recipients" value={total} iconClass="fas fa-users" gradient="primary" />
          <StatCard label="Sent" value={sent} iconClass="fas fa-paper-plane" gradient="info" />
          <StatCard label="Pending" value={pending} iconClass="fas fa-hourglass-half" gradient={pending > 0 ? "warning" : "success"} />
          <StatCard label="Failed" value={failed} iconClass="fas fa-times-circle" gradient="danger" />
        </div>

        {/* Progress bar */}
        <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef] p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-semibold text-[#32325d]">Campaign Progress</h4>
              <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${campaignStatusStyle(status)}`}>
                {status === "running" && <i className="fas fa-circle-notch fa-spin mr-1 text-[10px]" />}
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </span>
            </div>
            <span className="text-sm font-bold text-[#1171ef]">{pct}% complete</span>
          </div>

          <div className="h-3 rounded-full overflow-hidden bg-[#e9ecef] flex">
            <div
              className="bg-[#2dce89] transition-all duration-500 ease-out"
              style={{ width: `${total > 0 ? (sent / total) * 100 : 0}%` }}
            />
            <div
              className="bg-[#f5365c] transition-all duration-500 ease-out"
              style={{ width: `${total > 0 ? (failed / total) * 100 : 0}%` }}
            />
          </div>

          {/* Inline Resend for completed/failed */}
          {(status === "completed" || status === "failed") && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#e9ecef]">
              <span className="text-xs text-[#8898aa]">
                {status === "completed"
                  ? `Campaign finished — ${sent} sent, ${failed} failed.`
                  : `Campaign failed — ${failed} messages could not be sent.`}
                {" "}Resend to send again to all contacts.
              </span>
              <button
                onClick={handleResend}
                disabled={actionLoading}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1171ef] text-white text-sm font-medium hover:bg-[#4a5cd4] transition-colors disabled:opacity-50 shadow-sm"
              >
                <i className="fas fa-redo" />
                {actionLoading ? "Starting…" : "Resend Campaign"}
              </button>
            </div>
          )}

          {/* Inline Pause / Resume */}
          {(status === "running" || status === "paused") && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#e9ecef]">
              <span className="text-xs text-[#8898aa]">
                {status === "running"
                  ? `Sending message ${sent + 1} of ${total}…`
                  : `Paused after ${sent} messages. ${pending} remaining.`}
              </span>
              {status === "running" ? (
                <button
                  onClick={handlePause}
                  disabled={actionLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#fb6340] text-white text-sm font-medium hover:bg-[#e85a38] transition-colors disabled:opacity-50 shadow-sm"
                >
                  <i className="fas fa-pause" />
                  {actionLoading ? "Pausing…" : "Pause Campaign"}
                </button>
              ) : (
                <button
                  onClick={handleResume}
                  disabled={actionLoading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2dce89] text-white text-sm font-medium hover:bg-[#24a974] transition-colors disabled:opacity-50 shadow-sm"
                >
                  <i className="fas fa-play" />
                  {actionLoading ? "Resuming…" : "Resume Campaign"}
                </button>
              )}
            </div>
          )}

          <div className="flex gap-5 mt-3 text-xs text-[#8898aa]">
            <span><span className="inline-block w-2.5 h-2.5 bg-[#2dce89] rounded-full mr-1.5" />{sent} Sent</span>
            <span><span className="inline-block w-2.5 h-2.5 bg-[#e9ecef] border border-[#cad1d7] rounded-full mr-1.5" />{pending} Pending</span>
            <span><span className="inline-block w-2.5 h-2.5 bg-[#f5365c] rounded-full mr-1.5" />{failed} Failed</span>
            {(campaign as any).delayMin != null && (
              <span className="ml-auto">
                <i className="fas fa-clock mr-1" />Delay: {(campaign as any).delayMin}–{(campaign as any).delayMax}s per message
              </span>
            )}
          </div>
        </div>

        {/* Message log */}
        <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef]">
          <div className="px-5 py-4 border-b border-[#e9ecef] flex items-center justify-between">
            <h4 className="text-sm font-semibold text-[#32325d]">Message Log</h4>
            {isLive(status) && (
              <span className="text-xs text-[#8898aa] flex items-center gap-1.5">
                <i className="fas fa-circle-notch fa-spin text-[#1171ef]" />Auto-refreshing
              </span>
            )}
          </div>
          {(messages?.length ?? 0) === 0 ? (
            <div className="p-12 text-center">
              <i className="fas fa-inbox text-4xl text-[#cad1d7] mb-3 block" />
              <p className="text-[#8898aa] text-sm">No messages logged yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">#</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">Recipient</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">Message</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">Status</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">Sent At</th>
                  </tr>
                </thead>
                <tbody>
                  {messages?.map((m, i) => (
                    <tr key={m.id} className="border-b border-[#f6f9fc] hover:bg-[#f8f9fe]">
                      <td className="px-5 py-3 text-xs text-[#8898aa]">{i + 1}</td>
                      <td className="px-5 py-3">
                        <p className="text-sm font-medium text-[#32325d]">{(m as any).contactName ?? m.phoneNumber}</p>
                        <p className="text-xs text-[#8898aa]">{m.phoneNumber}</p>
                      </td>
                      <td className="px-5 py-3 text-sm text-[#525f7f] max-w-[220px] truncate">{m.content}</td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${msgStatusStyle(m.status)}`}>
                          {m.status.charAt(0).toUpperCase() + m.status.slice(1)}
                        </span>
                        {m.errorMessage && (
                          <p className="text-xs text-[#f5365c] mt-0.5 truncate max-w-[140px]" title={m.errorMessage}>{m.errorMessage}</p>
                        )}
                      </td>
                      <td className="px-5 py-3 text-xs text-[#8898aa]">
                        {m.sentAt ? new Date(m.sentAt).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
